/**
 * Project Context Provider
 * Manages the current project selection across the application
 * Projects are filtered based on the active pane (product/success/sales)
 *
 * URL Architecture (workspace-first):
 * - /w/:wsSlug/:prefix/:shortId/:path — project-scoped
 * - /w/:wsSlug/:prefix/:path — workspace-scoped (no shortId)
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { projectsApi, type Project, type PaneType } from '../api/projects';
import { usePane, type PaneType as UIPaneType } from '../shared/contexts/PaneContext';
import { WORKSPACE_SCOPED_PREFIXES } from '../shared/route-registry';
import { useWorkspace } from './WorkspaceContext';

const STORAGE_KEY = 'martha.project.selected';
const BOARD_STORAGE_KEY_PREFIX = 'martha.workspace.board';

const getBoardStorageKey = (projectId: string | null) =>
  projectId ? `${BOARD_STORAGE_KEY_PREFIX}.${projectId}` : BOARD_STORAGE_KEY_PREFIX;

/**
 * Extract short ID for URLs from full project ID
 * Takes the last 7 chars of the part after underscore for URL-friendly IDs
 * e.g., proj_fbykwkxd4o → kwkxd4o
 */
export function getProjectShortId(projectId: string): string {
  const parts = projectId.split('_');
  const idPart = parts[1] || projectId;
  return idPart.slice(-7);
}

interface ProjectContextValue {
  // Current selection
  projectId: string | null;
  project: Project | null;
  /** Alias for `project` — used by GTM pane pages */
  currentProject: Project | null;

  // Short ID for URL-friendly identification
  shortId: string | null;

  // Projects filtered by active pane
  projects: Project[];
  // All projects unfiltered
  allProjects: Project[];

  isLoading: boolean;
  error: string | null;

  // Actions
  setProjectId: (id: string | null) => void;
  setProjectByShortId: (shortId: string) => boolean;
  refreshProjects: () => Promise<void>;

  // URL helpers
  buildProjectUrl: (path: string, prefix?: string) => string;
  findProjectByShortId: (shortId: string) => Project | null;

  // Board selection (project-scoped, migrated from WorkspaceContext)
  boardId: string | null;
  setBoardId: (id: string | null) => void;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

/**
 * Maps UI pane type to project pane type
 * UI uses 'product', projects use 'development'
 */
function mapUIPaneToProjectPane(uiPane: UIPaneType): PaneType {
  if (uiPane === 'product') return 'development';
  if (uiPane === 'sales') return 'gtm';
  if (uiPane === 'ops') return 'development'; // ops shows all dev projects
  return uiPane as PaneType;
}

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activePane } = usePane();
  const { currentWorkspace, wsSlug } = useWorkspace();

  // Workspace ID derived from WorkspaceContext
  const workspaceId = currentWorkspace?.id ?? null;

  // Load initial project ID from localStorage
  const [projectId, setProjectIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Board state (migrated from WorkspaceContext) ──────────────────────
  const [boardId, setBoardIdState] = useState<string | null>(() => {
    try {
      const projectKey = getBoardStorageKey(null);
      return localStorage.getItem(projectKey);
    } catch {
      return null;
    }
  });

  // Track previous project ID to restore project-scoped board selection
  const prevProjectIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const currentProjectId = projectId;
    const prevProjectId = prevProjectIdRef.current;

    if (prevProjectId !== undefined && currentProjectId !== prevProjectId) {
      const savedBoard = localStorage.getItem(getBoardStorageKey(currentProjectId));
      setBoardIdState(savedBoard);
    }
    prevProjectIdRef.current = currentProjectId;
  }, [projectId]);

  const setBoardId = useCallback((id: string | null) => {
    setBoardIdState(id);
    try {
      const key = getBoardStorageKey(projectId);
      if (id) {
        localStorage.setItem(key, id);
      } else {
        localStorage.removeItem(key);
      }
    } catch (err) {
      console.error('Failed to save board selection:', err);
    }
  }, [projectId]);

  // ── Project loading ───────────────────────────────────────────────────
  const refreshProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await projectsApi.listAll();
      setAllProjects(list);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  // Filter projects by workspace then by active pane
  const projectPane = mapUIPaneToProjectPane(activePane);
  const projects = useMemo(() => {
    const workspaceFiltered = allProjects.filter(p => {
      if (workspaceId && p.workspaceId && p.workspaceId !== workspaceId) return false;
      return true;
    });
    const paneFiltered = workspaceFiltered.filter(p => {
      if (!p.panes || p.panes.length === 0) return true;
      return p.panes.includes(projectPane);
    });
    return paneFiltered.length > 0 ? paneFiltered : workspaceFiltered;
  }, [allProjects, projectPane, workspaceId]);

  // Get current project object (search all projects so cross-pane selection works)
  const project = useMemo(() => {
    return allProjects.find(p => p.id === projectId) || null;
  }, [allProjects, projectId]);

  // Compute shortId
  const shortId = useMemo(() => {
    if (project) return getProjectShortId(project.id);
    if (projectId) return getProjectShortId(projectId);
    return null;
  }, [project, projectId]);

  // Find project by shortId or slug
  const findProjectByShortId = useCallback((searchShortIdOrSlug: string): Project | null => {
    const byShortId = allProjects.find(p => getProjectShortId(p.id) === searchShortIdOrSlug);
    if (byShortId) return byShortId;
    return allProjects.find(p => p.slug === searchShortIdOrSlug) || null;
  }, [allProjects]);

  // Project selection is now driven exclusively by URL.
  // ProjectRouter calls setProjectByShortId() when URL has :shortId.
  // No auto-select — if no project in URL, project is null.

  // Set project ID and persist to localStorage
  const setProjectId = useCallback((id: string | null) => {
    setProjectIdState(id);
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error('Failed to save project selection:', err);
    }
  }, []);

  // Set project by shortId or slug (returns true if found)
  const setProjectByShortId = useCallback((searchShortIdOrSlug: string): boolean => {
    let foundProject = allProjects.find(p => getProjectShortId(p.id) === searchShortIdOrSlug);
    if (!foundProject) {
      foundProject = allProjects.find(p => p.slug === searchShortIdOrSlug);
    }
    if (foundProject) {
      setProjectId(foundProject.id);
      return true;
    }
    return false;
  }, [allProjects, setProjectId]);

  // Build pane URL with workspace prefix.
  // Workspace-scoped panes (sales, archie, success) omit the project shortId segment.
  const buildProjectUrl = useCallback((path: string, prefix: string = 'product'): string => {
    const cleanPath = path.replace(/^\/(?:product|pr|gt|su|success|sales|p)\/[^/]+\/?/, '').replace(/^\/+/, '');
    if (wsSlug && WORKSPACE_SCOPED_PREFIXES.has(prefix)) {
      return `/w/${wsSlug}/${prefix}/${cleanPath}`;
    }
    if (wsSlug && shortId) {
      return `/w/${wsSlug}/${prefix}/${shortId}/${cleanPath}`;
    }
    if (!shortId) {
      return path.startsWith('/') ? path : `/${path}`;
    }
    return `/${prefix}/${shortId}/${cleanPath}`;
  }, [wsSlug, shortId]);

  // Project selection is URL-driven. No cross-tab or workspace-changed listeners needed.

  const value = useMemo<ProjectContextValue>(() => ({
    projectId,
    project,
    currentProject: project,
    shortId,
    projects,
    allProjects,
    isLoading,
    error,
    setProjectId,
    setProjectByShortId,
    refreshProjects,
    buildProjectUrl,
    findProjectByShortId,
    boardId,
    setBoardId,
  }), [
    projectId, project, shortId, projects, allProjects,
    isLoading, error, setProjectId, setProjectByShortId,
    refreshProjects, buildProjectUrl, findProjectByShortId,
    boardId, setBoardId,
  ]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

/**
 * Hook to access the project context
 */
export const useProject = (): ProjectContextValue => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

/**
 * Hook to get just the project ID (for simple usage)
 */
export const useProjectId = (): string | null => {
  const { projectId } = useProject();
  return projectId;
};

/**
 * Hook to get the project shortId (for URL building)
 */
export const useProjectShortId = (): string | null => {
  const { shortId } = useProject();
  return shortId;
};
